import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { getSEOData } from '../config/seo';
import { trackEvent } from '../components/GoogleAnalytics';
import { 
  Brain, Database, BarChart3, Zap, Settings, Shield, 
  MessageSquare, Eye, Target, Cog, Users, Lock,
  ArrowRight, CheckCircle, Play, Star, TrendingUp,
  Code, Layers, GitBranch, Clock, Bell, FileText,
  Workflow, Calendar, Bot, ChevronDown, ChevronUp
} from 'lucide-react';

const Features = () => {
  const seoData = getSEOData('/features');
  const [activeCategory, setActiveCategory] = useState('ai-intelligence');
  const [expandedFeature, setExpandedFeature] = useState(null);

  // Track page view and feature category changes
  useEffect(() => {
    trackEvent('page_view', 'engagement', 'features_page');
  }, []);

  useEffect(() => {
    trackEvent('feature_category_view', 'engagement', activeCategory);
  }, [activeCategory]);

  const featureCategories = [
    {
      id: 'ai-intelligence',
      name: 'AI & Intelligence',
      icon: Brain,
      color: 'from-purple-500 to-pink-500',
      description: 'Conversational AI that understands your business and automates complex operations',
      features: [
        {
          name: 'Conversational AI',
          description: 'Natural language queries to interact with your data and systems',
          capabilities: [
            'Ask questions in plain English',
            'Get instant insights from your data',
            'Execute operations through conversation',
            'Context-aware responses with conversation history'
          ],
          endpoints: ['askQuestion', 'processQuery', 'createConversation'],
          useCases: [
            'Query sales data: "Show me top customers this quarter"',
            'Generate reports: "Create a monthly revenue report"',
            'Data analysis: "What are the trends in customer behavior?"'
          ]
        },
        {
          name: 'Smart Analytics & Insights',
          description: 'AI-powered data analysis that identifies patterns and anomalies',
          capabilities: [
            'Automated insight generation',
            'Anomaly detection',
            'Predictive analytics',
            'Performance recommendations'
          ],
          endpoints: ['getInsights', 'getOperationHistory'],
          useCases: [
            'Identify declining customer satisfaction trends',
            'Predict inventory shortages before they happen',
            'Recommend process optimizations'
          ]
        },
        {
          name: 'Autonomous Operations',
          description: 'AI that can perform operations with different levels of autonomy',
          capabilities: [
            'Cautious mode: Ask before executing',
            'Balanced mode: Execute with confirmation',
            'Autonomous mode: Full automation',
            'Undo capabilities for all operations'
          ],
          endpoints: ['setAutonomyLevel', 'undoOperation', 'getUndoableOperations'],
          useCases: [
            'Automatically update records based on conditions',
            'Generate and send reports on schedule',
            'Manage data cleanup and maintenance'
          ]
        }
      ]
    },
    {
      id: 'data-management',
      name: 'Data Management',
      icon: Database,
      color: 'from-blue-500 to-cyan-500',
      description: 'Complete data lifecycle management with powerful CRUD operations',
      features: [
        {
          name: 'Object & Record Management',
          description: 'Full lifecycle management of business objects and records',
          capabilities: [
            'Create, read, update, delete operations',
            'Bulk data processing',
            'Data validation and integrity',
            'Record relationships and lookups'
          ],
          endpoints: ['getObjectListData', 'saveObjectRecord', 'getRecordDetails', 'deleteRecord'],
          useCases: [
            'Manage customer records with complete history',
            'Bulk import/export of product catalogs',
            'Maintain complex organizational hierarchies'
          ]
        },
        {
          name: 'Advanced Data Operations',
          description: 'Sophisticated data manipulation and relationship management',
          capabilities: [
            'Complex queries and filtering',
            'Data relationships and joins',
            'Custom field types and validation',
            'Data transformation and mapping'
          ],
          endpoints: ['getObjectRecord', 'getObjectRecordType', 'invokeFunction'],
          useCases: [
            'Create multi-level approval workflows',
            'Manage inventory with supplier relationships',
            'Track project dependencies and timelines'
          ]
        },
        {
          name: 'Metadata Management',
          description: 'Dynamic schema management and object customization',
          capabilities: [
            'Dynamic object creation',
            'Custom field definitions',
            'Schema evolution and versioning',
            'Object relationship mapping'
          ],
          endpoints: ['getOrgObjects', 'getObjectAndFieldDetails'],
          useCases: [
            'Adapt to changing business requirements',
            'Create industry-specific data models',
            'Manage complex compliance requirements'
          ]
        }
      ]
    },
    {
      id: 'reporting-analytics',
      name: 'Reporting & Analytics',
      icon: BarChart3,
      color: 'from-green-500 to-emerald-500',
      description: 'Advanced reporting engine with interactive dashboards and real-time analytics',
      features: [
        {
          name: 'Custom Report Builder',
          description: 'Drag-and-drop report creation with advanced filtering and grouping',
          capabilities: [
            'Visual report designer',
            'Advanced filtering and conditions',
            'Grouping and summarization',
            'Custom parameters and variables'
          ],
          endpoints: ['getAllReports', 'saveReport', 'executeReport'],
          useCases: [
            'Sales performance reports by region',
            'Financial statements with drill-down',
            'Operational KPI dashboards'
          ]
        },
        {
          name: 'Interactive Dashboards',
          description: 'Real-time dashboards with customizable widgets and charts',
          capabilities: [
            'Drag-and-drop dashboard builder',
            'Real-time data updates',
            'Interactive charts and widgets',
            'Mobile-responsive design'
          ],
          endpoints: ['getAllDashboards', 'saveDashboard'],
          useCases: [
            'Executive dashboards for C-suite',
            'Operational monitoring centers',
            'Customer service performance tracking'
          ]
        },
        {
          name: 'Data Export & Integration',
          description: 'Seamless data export and integration capabilities',
          capabilities: [
            'Excel/CSV export functionality',
            'Scheduled report delivery',
            'API-based data sharing',
            'Real-time data synchronization'
          ],
          endpoints: ['downloadReportData'],
          useCases: [
            'Monthly board report generation',
            'Automated compliance reporting',
            'Data integration with external systems'
          ]
        }
      ]
    },
    {
      id: 'workflow-automation',
      name: 'Workflow & Automation',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500',
      description: 'Powerful automation engine for business processes and workflows',
      features: [
        {
          name: 'Process Automation',
          description: 'Visual workflow designer for complex business processes',
          capabilities: [
            'Drag-and-drop workflow designer',
            'Multi-stage approval processes',
            'Conditional logic and branching',
            'Error handling and retry mechanisms'
          ],
          endpoints: ['saveAutomaton', 'getAllAutomatons'],
          useCases: [
            'Employee onboarding workflows',
            'Purchase order approval processes',
            'Customer support escalation procedures'
          ]
        },
        {
          name: 'Scheduled Jobs & Tasks',
          description: 'Automated task scheduling with flexible timing options',
          capabilities: [
            'Cron-based scheduling',
            'Interval-based execution',
            'Job monitoring and logging',
            'Failure notifications and alerts'
          ],
          endpoints: ['getAllJobs', 'createScheduledJob', 'activateDeactivateJob'],
          useCases: [
            'Daily data backup procedures',
            'Monthly report generation',
            'Periodic system maintenance tasks'
          ]
        },
        {
          name: 'Custom Functions & Triggers',
          description: 'Create custom business logic with event-driven triggers',
          capabilities: [
            'Visual function builder',
            'Event-based triggers (onCreate, onUpdate, onDelete)',
            'Custom validation rules',
            'Integration with external services'
          ],
          endpoints: ['getAllFunctions', 'saveFunction', 'compileFunction'],
          useCases: [
            'Automatic invoice generation on order completion',
            'Real-time inventory updates',
            'Customer notification systems'
          ]
        }
      ]
    },
    {
      id: 'custom-development',
      name: 'Custom Development',
      icon: Settings,
      color: 'from-indigo-500 to-purple-500',
      description: 'No-code/low-code development platform for custom applications',
      features: [
        {
          name: 'Visual Page Builder',
          description: 'Drag-and-drop interface for creating custom application pages',
          capabilities: [
            'Component-based page design',
            'Responsive layout system',
            'Custom styling and themes',
            'Interactive form builders'
          ],
          endpoints: ['fetchAllPages', 'fetchPageDetail'],
          useCases: [
            'Custom application interfaces',
            'Client portals and dashboards',
            'Mobile-responsive forms'
          ]
        },
        {
          name: 'API Integration Platform',
          description: 'Connect with external services and systems seamlessly',
          capabilities: [
            'REST API integrations',
            'Webhook management',
            'Data transformation pipelines',
            'Authentication handling'
          ],
          endpoints: ['invokeFunction'],
          useCases: [
            'CRM system integrations',
            'Payment gateway connections',
            'Third-party service automation'
          ]
        },
        {
          name: 'Package Management',
          description: 'Create, deploy, and manage application packages',
          capabilities: [
            'Component packaging system',
            'Version control and deployment',
            'Environment management',
            'Dependency resolution'
          ],
          endpoints: ['getOrgPackages', 'createUpdatePackage', 'compilePackage'],
          useCases: [
            'Multi-environment deployments',
            'Reusable component libraries',
            'Application versioning and rollbacks'
          ]
        }
      ]
    },
    {
      id: 'security-collaboration',
      name: 'Security & Collaboration',
      icon: Shield,
      color: 'from-red-500 to-pink-500',
      description: 'Enterprise-grade security with team collaboration features',
      features: [
        {
          name: 'Role-Based Access Control',
          description: 'Granular permissions and security management',
          capabilities: [
            'User role management',
            'Object-level permissions',
            'Field-level security',
            'API access control'
          ],
          endpoints: ['getOrgGroups', 'createUpdateGroup'],
          useCases: [
            'Department-specific data access',
            'Client portal permissions',
            'Audit trail management'
          ]
        },
        {
          name: 'Sharing Rules & Policies',
          description: 'Advanced data sharing and privacy controls',
          capabilities: [
            'Conditional sharing rules',
            'Data privacy compliance',
            'Cross-organizational sharing',
            'Temporary access grants'
          ],
          endpoints: ['getAllSharingRules', 'createUpdateSharingRule'],
          useCases: [
            'Client data sharing agreements',
            'Partner portal access',
            'Compliance reporting access'
          ]
        },
        {
          name: 'Team Collaboration',
          description: 'Built-in collaboration tools for team productivity',
          capabilities: [
            'Record-level discussions',
            'Comment threads and mentions',
            'Activity feeds and notifications',
            'Document sharing and versioning'
          ],
          endpoints: ['getPosts', 'savePostOnRecord', 'getComments'],
          useCases: [
            'Project collaboration spaces',
            'Customer service case discussions',
            'Sales opportunity tracking'
          ]
        }
      ]
    }
  ];

  const stats = [
                  { number: 'Global', label: 'Enterprise Clients', icon: Users },
    { number: '1000+', label: 'Custom Applications', icon: Layers },
    { number: '99.9%', label: 'Uptime SLA', icon: TrendingUp },
    { number: '24/7', label: 'Support Available', icon: Clock }
  ];

  const testimonials = [
    {
      quote: "Aquargin's AI capabilities have transformed how we handle data analysis. What used to take hours now happens in minutes.",
      name: "",
      title: "",
      company: "TechCorp Industries",
      avatar: "👩‍💼"
    },
    {
      quote: "The workflow automation features have eliminated manual processes and reduced errors by 95% across our organization.",
      name: "",
      title: "",
      company: "Global Manufacturing",
      avatar: "👨‍💼"
    },
    {
      quote: "Building custom applications without coding has empowered our business teams to solve problems independently.",
      name: "",
      title: "",
      company: "Financial Services Ltd",
      avatar: "👩‍💻"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...seoData} />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Composable Enterprise
              <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Platform Capabilities
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 mb-10 leading-relaxed">
              Modular, API-first capabilities that form packaged business components. Build, orchestrate, 
              and scale enterprise solutions with composable architecture designed for rapid adaptation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="https://www.youtube.com/@nocodeAquargin"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="flex justify-center mb-3">
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-1">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Interface Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Platform Interface Gallery
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our intuitive interfaces designed for enterprise productivity and ease of use
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/no-code.png"
                  alt="No-Code Platform Builder"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">No-Code Builder</h3>
              <p className="text-gray-600 mt-2">Build applications without coding</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/detail-view.png"
                  alt="Detailed Record View"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Detail Views</h3>
              <p className="text-gray-600 mt-2">Comprehensive record management</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/Permission-access-control.png"
                  alt="Permission Access Control"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Access Control</h3>
              <p className="text-gray-600 mt-2">Granular permission management</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/import-export-module.png"
                  alt="Import Export Module"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Data Import/Export</h3>
              <p className="text-gray-600 mt-2">Seamless data migration tools</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/No-code-page-builder.png"
                  alt="Page Builder Interface"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Page Builder</h3>
              <p className="text-gray-600 mt-2">Visual page creation tools</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/Executive_dashboard_showing_advanced_reporting_cap_1750596653.png"
                  alt="Advanced Reporting Dashboard"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Advanced Reporting</h3>
              <p className="text-gray-600 mt-2">Executive-level analytics</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Categories Navigation */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Composable Business Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore modular, reusable components that can be assembled into custom enterprise solutions. 
              Each capability is API-first, independently scalable, and designed for composable architecture.
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {featureCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeCategory === category.id
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <category.icon className="h-5 w-5" />
                <span>{category.name}</span>
              </button>
            ))}
          </div>

          {/* Active Category Content */}
          {featureCategories.map((category) => (
            activeCategory === category.id && (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl mx-auto"
              >
                <div className="text-center mb-12">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${category.color} text-white mb-4`}>
                    <category.icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{category.name}</h3>
                  <p className="text-xl text-gray-600">{category.description}</p>
                </div>

                <div className="space-y-8">
                  {category.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h4 className="text-2xl font-bold text-gray-900 mb-2">{feature.name}</h4>
                          <p className="text-gray-600 text-lg">{feature.description}</p>
                        </div>
                        <button
                          onClick={() => setExpandedFeature(expandedFeature === `${category.id}-${index}` ? null : `${category.id}-${index}`)}
                          className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {expandedFeature === `${category.id}-${index}` ? (
                            <ChevronUp className="h-6 w-6" />
                          ) : (
                            <ChevronDown className="h-6 w-6" />
                          )}
                        </button>
                      </div>

                      <div className="grid lg:grid-cols-2 gap-8">
                        <div>
                          <h5 className="font-semibold text-gray-900 mb-4 flex items-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                            Key Capabilities
                          </h5>
                          <ul className="space-y-2">
                            {feature.capabilities.map((capability, idx) => (
                              <li key={idx} className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                                <span className="text-gray-700">{capability}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h5 className="font-semibold text-gray-900 mb-4 flex items-center">
                            <Target className="h-5 w-5 text-blue-500 mr-2" />
                            Use Cases
                          </h5>
                          <ul className="space-y-2">
                            {feature.useCases.map((useCase, idx) => (
                              <li key={idx} className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                                <span className="text-gray-700">{useCase}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {expandedFeature === `${category.id}-${index}` && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-8 pt-8 border-t border-gray-200"
                        >
                          <h5 className="font-semibold text-gray-900 mb-4 flex items-center">
                            <Code className="h-5 w-5 text-purple-500 mr-2" />
                            API Endpoints
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            {feature.endpoints.map((endpoint, idx) => (
                              <span
                                key={idx}
                                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-mono"
                              >
                                {endpoint}
                              </span>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how organizations are transforming their operations with Aquargin's features
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center">
                  <div className="text-3xl mr-3">{testimonial.avatar}</div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.title}</div>
                    <div className="text-sm text-gray-500">{testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Experience These Features?
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Start your free trial today and discover how Aquargin's comprehensive platform 
              can transform your business operations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start 30-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Schedule Demo
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No credit card required  ✓ Full feature access  ✓ 24/7 support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Features; 